"use client";

import type { BillingPlanType } from "@workspace/db/constants/pricing";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@workspace/ui/components/alert-dialog";
import { Button, buttonVariants } from "@workspace/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import { toast } from "@workspace/ui/components/sonner";
import { Spinner } from "@workspace/ui/components/spinner";
import { AlertTriangle } from "lucide-react";
import { useTransition } from "react";

import { notifyPaymentAction } from "../payment-notification.actions";
import type { SubscriptionInvoice } from "../subscription-invoices";
import { SubscriptionInvoicesSection } from "./subscription-invoices-section";

type SubscriptionBlockedContentProps = {
  orgSlug: string;
  billingPeriod?: "monthly" | "annual" | null;
  paymentMethod?: "mercadopago" | "bank_transfer" | "rebill" | null;
  planType?: BillingPlanType | null;
  reason:
    | "trial_expired"
    | "subscription_incomplete"
    | "subscription_paused"
    | "subscription_canceled"
    | "payment_overdue";
  subscriptionStartedAt?: Date | null;
  customSubscriptionLink?: string | null;
  subscriptionInvoices?: SubscriptionInvoice[];
};

const copyByReason: Record<
  SubscriptionBlockedContentProps["reason"],
  { title: string; description: string }
> = {
  trial_expired: {
    title: "Tu prueba terminó",
    description:
      "Necesitás una suscripción activa para seguir usando La Pyme. Podés elegir un plan ahora o cambiar de organización desde el menú superior.",
  },
  subscription_incomplete: {
    title: "La suscripción todavía no está activa",
    description:
      "Todavía no pudimos completar la activación inicial. Escribinos para terminar la configuración y seguir usando La Pyme.",
  },
  subscription_paused: {
    title: "La suscripción está pausada",
    description:
      "La facturación quedó pausada para esta organización. Escribinos para reactivarla y volver a usar La Pyme.",
  },
  subscription_canceled: {
    title: "La suscripción está vencida",
    description:
      "Renová la suscripción para seguir usando La Pyme. También podés cambiar de organización desde el menú superior.",
  },
  payment_overdue: {
    title: "Tu pago está vencido",
    description:
      "No pudimos confirmar tu último pago. Regularizá tu situación para seguir usando La Pyme.",
  },
};

export function SubscriptionBlockedContent({
  orgSlug,
  paymentMethod,
  reason,
  customSubscriptionLink,
  subscriptionInvoices = [],
}: SubscriptionBlockedContentProps) {
  const [isNotifyingPayment, startNotifyPaymentTransition] = useTransition();
  const canRecoverWithOnlinePayment =
    paymentMethod === "rebill" || paymentMethod === "mercadopago";
  const shouldShowOnlinePaymentCta =
    canRecoverWithOnlinePayment &&
    (reason === "payment_overdue" ||
      reason === "subscription_paused" ||
      reason === "subscription_incomplete" ||
      reason === "subscription_canceled");
  const { title, description } = shouldShowOnlinePaymentCta
    ? {
        title: "No pudimos procesar tu pago",
        description:
          "Para mantener la suscripción y volver a usar La Pyme, reintentá el pago.",
      }
    : copyByReason[reason];

  function handleNotifyPayment() {
    startNotifyPaymentTransition(async () => {
      const result = await notifyPaymentAction(orgSlug);
      if (result.success) {
        toast.success("Pago informado correctamente");
      } else {
        toast.error(result.error);
      }
    });
  }

  return (
    <div className="flex flex-1 justify-center py-12">
      <div className="w-full max-w-2xl space-y-4">
        <Card>
          <CardHeader className="space-y-4 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="size-6" />
            </div>
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription className="text-base text-muted-foreground">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            {shouldShowOnlinePaymentCta ? (
              <a
                className={buttonVariants({})}
                href={`/suscribirse/${orgSlug}`}
              >
                Ir a pagar
              </a>
            ) : reason === "payment_overdue" &&
              paymentMethod === "bank_transfer" ? (
              <AlertDialog>
                <AlertDialogTrigger
                  render={
                    <Button disabled={isNotifyingPayment} type="button" />
                  }
                >
                  Informar transferencia
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      ¿Informaste el pago por transferencia?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Vamos a marcar la factura como pendiente de confirmación.
                      Vas a poder seguir usando La Pyme mientras confirmamos el
                      pago.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isNotifyingPayment}>
                      Cancelar
                    </AlertDialogCancel>
                    <AlertDialogAction
                      disabled={isNotifyingPayment}
                      onClick={handleNotifyPayment}
                    >
                      {isNotifyingPayment ? (
                        <Spinner aria-hidden data-icon="inline-start" />
                      ) : null}
                      Informar pago
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <a
                className={buttonVariants({})}
                href={customSubscriptionLink ?? `/suscribirse/${orgSlug}`}
              >
                Suscribirse
              </a>
            )}
            <a
              className={buttonVariants({ variant: "outline" })}
              href="mailto:hola@lapyme.com.ar"
            >
              Escribinos
            </a>
          </CardContent>
        </Card>
        {subscriptionInvoices.length > 0 ? (
          <SubscriptionInvoicesSection
            description="Revisá las facturas pendientes antes de informar la transferencia."
            invoices={subscriptionInvoices}
            orgSlug={orgSlug}
            title="Facturas pendientes"
          />
        ) : null}
      </div>
    </div>
  );
}
